// The household's own copy of each recipe.
//
// Matt, 2026-10-02: the teacher "should be able to modify the recipe with
// full understanding of the parameters". A recipe in code (curriculum/
// lessons.js) can only change with a deploy — tonight's five-chop version
// was exactly that. So each hat gets its own copy at
// <hat>/cooking/recipes/<id>, seeded from lessons.js the first time it is
// read and never re-seeded (a revision is theirs to keep). The lesson page
// and cook mode read the copy, live; the teacher's revise_recipe action
// replaces whole parts of it and logs why under `revisions`.
import { ref, watch, onBeforeUnmount } from 'vue';
import { lessonById } from '../curriculum/lessons.js';
import { dbGet, dbPut, dbPost } from '../store/db.js';
import { subscribe } from './live.js';
import { coursePath } from './course.js';

export const recipePath = (hat, id) => `${coursePath(hat)}/recipes/${id}`;

// Firebase drops empty arrays and objects, so a stored recipe can come back
// without `do`, `order` or `equipment`; put the shape back.
export function normalizeRecipe (raw, base) {
  if (!raw) return base;
  const list = (v) => (Array.isArray(v) ? v : v && typeof v === 'object' ? Object.values(v) : []);
  return {
    ...base,
    ...raw,
    order: list(raw.order),
    grocery: list(raw.grocery),
    equipment: list(raw.equipment),
    mise: list(raw.mise).map((m) => (typeof m === 'string' ? { text: m, needs: baseNeeds(m, base, 'mise') } : { ...m, needs: m.needs ? list(m.needs) : baseNeeds(m.text, base, 'mise') })),
    steps: list(raw.steps).map((s) => (typeof s === 'string' ? { text: s, needs: baseNeeds(s, base) } : { ...s, do: list(s.do), needs: s.needs ? list(s.needs) : baseNeeds(s.text, base) }))
  };
}

/** A step as one line for the Ask box's /ask call: the words, what it uses, the moves. */
export const askStepLine = (step) => [
  step.text,
  step.needs?.length ? ` Needs: ${step.needs.join(', ')}.` : '',
  step.do?.length ? ` Hands-on: ${step.do.join('; ')}.` : ''
].join('');

// A copy seeded before steps (and then prep screens) carried `needs`
// (2026-10-12), or whose empty list Firebase dropped, borrows them from the
// curriculum step with the very same words. A step the teacher has rewritten
// gets none: no line is better than amounts for a step that no longer says
// that.
function baseNeeds (text, base, part = 'steps') {
  return [...(base?.[part]?.find((b) => b.text === text)?.needs || [])];
}

/** Seeds the hat's copy from the curriculum if it has none. Returns the copy. */
export async function ensureRecipe (hat, id) {
  const base = lessonById(id);
  if (!hat || !base) return base || null;
  const stored = await dbGet(recipePath(hat, id)).catch(() => null);
  if (stored) return normalizeRecipe(stored, base);
  const seed = { ...base, seededAt: Date.now() };
  await dbPut(recipePath(hat, id), seed).catch(() => {});
  return seed;
}

// The word "pan" must never reach a screen (CLAUDE.md: it puts iOS into
// card-autofill mode and takes the dictation mic). The teacher is told; this
// is the belt to that brace, on everything it writes.
const PAN = /\b(p)an\b/gi;
export const depan = (text) => String(text).replace(PAN, (m, p) => (p === 'P' ? 'Skillet' : 'skillet'));
export function depanDeep (value) {
  if (typeof value === 'string') return depan(value);
  if (Array.isArray(value)) return value.map(depanDeep);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, depanDeep(v)]));
  return value;
}

/**
 * Edits to one list of the recipe, item by item. The teacher used to send a
 * whole new prep list, and on 2026-10-12 it sent one holding only the step it
 * was changing (an exact salt amount): the preheat, the potatoes, the onion,
 * the garlic, the broccoli and both trays vanished mid-lesson, and the oven
 * was never told to heat. So a list is never replaced any more - only edited.
 * Each edit names an index in the list AS IT IS NOW (before any of these
 * edits): `replace` swaps that item, `remove` drops it, `insert` puts `item`
 * before it (an index equal to the length appends).
 */
export function applyListEdits (list, edits) {
  const before = new Map();
  const replace = new Map();
  const remove = new Set();
  for (const e of edits || []) {
    const at = Number.isInteger(e?.at) ? Math.max(0, Math.min(e.at, list.length)) : list.length;
    if (e?.op === 'remove' && at < list.length) remove.add(at);
    else if (e?.op === 'replace' && at < list.length && e.item) replace.set(at, e.item);
    else if (e?.op === 'insert' && e.item) before.set(at, [...(before.get(at) || []), e.item]);
  }
  const out = [];
  for (let i = 0; i <= list.length; i += 1) {
    out.push(...(before.get(i) || []));
    if (i === list.length) break;
    if (remove.has(i)) continue;
    out.push(replace.has(i) ? replace.get(i) : list[i]);
  }
  return out;
}

/** Applies a revise_recipe action to the hat's copy; returns the patch written. */
export async function reviseRecipe (hat, id, action) {
  const parts = {};
  for (const key of ['minutes', 'order', 'grocery', 'equipment', 'plate']) {
    if (action[key] !== undefined && action[key] !== null) parts[key] = depanDeep(action[key]);
  }
  const current = await ensureRecipe(hat, id);
  if (!current) return null;
  // The prep and the steps change item by item (see applyListEdits).
  if (Array.isArray(action.mise_edits) && action.mise_edits.length) parts.mise = applyListEdits(current.mise || [], depanDeep(action.mise_edits));
  if (Array.isArray(action.step_edits) && action.step_edits.length) parts.steps = applyListEdits(current.steps || [], depanDeep(action.step_edits));
  if (!Object.keys(parts).length) return null;
  const next = { ...current, ...parts, revisedAt: Date.now() };
  await dbPut(recipePath(hat, id), next);
  await dbPost(`${recipePath(hat, id)}/revisions`, { at: Date.now(), reason: depan(action.reason || ''), parts: Object.keys(parts) }).catch(() => {});
  return parts;
}

/** Live recipe for a screen: the hat's copy, the curriculum until it loads. */
export function useRecipe (hatRef, idRef) {
  const recipe = ref(lessonById(idRef.value) || null);
  let unsubscribe = null;
  function follow () {
    unsubscribe?.();
    unsubscribe = null;
    const base = lessonById(idRef.value) || null;
    recipe.value = base;
    if (!hatRef.value || !base) return;
    ensureRecipe(hatRef.value, idRef.value).then((r) => { if (r) recipe.value = r; return r; }).catch(() => null);
    unsubscribe = subscribe(recipePath(hatRef.value, idRef.value), (data) => { if (data) recipe.value = normalizeRecipe(data, base); });
  }
  watch([hatRef, idRef], follow, { immediate: true });
  onBeforeUnmount(() => unsubscribe?.());
  return recipe;
}
